import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import { Button, Dialog, Grid, MenuItem, Popper, TextField, Tooltip, Typography } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import React, { useEffect, useState } from "react";
import { useSnackbar } from "../../components/context/SnackbarContext";
import useAxios from "../../hooks/useAxios";
import { useLoader } from "../../components/context/LoaderContext";
import { Formik } from "formik";
import * as yup from "yup";
import moment from "moment-timezone";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useSelector } from "react-redux";
import { PickerWrapper } from "../../utils/Utils";
import DynamicFields from "./CustomFields";

function AddTaskDialog({ onClose, open, type, dataId, handleReloadFunc, editId, priority, status }) {
    const axiosInstance = useAxios();
    const { showSnackbar } = useSnackbar();
    const { showLoader, hideLoader, loading } = useLoader();
    const [taskList, setTaskList] = useState([])
    const { preferredDateFormat, preTimeZoneKey } = useSelector((state) => state?.user);
    const [customFields, setCustomFields] = useState([]);

    //=======Start::Write a code for manage Edit Notes======//
    useEffect(() => {
        if (editId) {
            const fetchData = async () => {
                await getTask();
            };
            fetchData();
        } else {
            async function fetchData() {
                getData();
            }
            fetchData();
        }
    }, []);


    const getData = async () => {
        showLoader();
        await axiosInstance.get(`api/${type}-task/create/?${type}Id=${dataId}`).then((response) => {
            if (response.status === 200) {
                //console.log(response);
                setCustomFields(response?.data?.data?.customFields || []);
                hideLoader();
            }
        })
            .then((json) => { })
            .catch((error) => {
                if (error.response) {
                    hideLoader();
                    if (error.response.status === 400) {
                        showSnackbar(error.response.data.message, "warning");
                    }
                }
            });
    }

    async function getTask() {
        showLoader()
        try {
            const response = await axiosInstance.get(`api/${type}-task/edit/${editId}?${type}Id=${dataId}`);
            if (response.data.code === 200) {
                setTaskList(response.data.data.record);
                setCustomFields(response?.data?.data?.customFields || []);
            }
        } catch (error) {
            if (error.response) {
                showSnackbar(error.response.data.message, "warning");
            }
        } finally {
            hideLoader();
        }
    }
    //=======Start::Write a code for manage Edit Notes======//

    const buildInitialValues = (customFields) => {
        // Default initial values for standard fields
        const initial = {
            name: taskList?.name || '',
            description: taskList?.description || '',
            end_date: taskList?.end_date ? moment(taskList?.end_date) : moment(),
            priority: taskList?.priority || '',
            activity_status: taskList?.activity_status || '',
            error_list: [],
        };

        // Step 1: Map custom field values from the eventList
        const customValuesMap = {};

        if (Array.isArray(taskList?.custom_field_values)) {
            taskList.custom_field_values.forEach(item => {
                if (item?.field_name) {
                    customValuesMap[item.field_name.trim()] = item.value;
                }
            });
        }

        // Step 2: Initialize values for all custom fields
        customFields.forEach(field => {
            const key = field.field_name?.trim();
            const fieldValue = customValuesMap[key];

            switch (field.display_type) {
                case 'checkbox':
                    try {
                        // If it's a stored JSON string like '["a", "b"]'
                        initial[key] = fieldValue ? JSON.parse(fieldValue) : [];
                    } catch {
                        initial[key] = Array.isArray(fieldValue) ? fieldValue : [];
                    }
                    break;

                case 'file':
                    // You can customize this if you support showing uploaded file names
                    initial[key] = []; // Typically empty unless you want to preview old uploads
                    break;

                case 'date':
                case 'datetime':
                    initial[key] = fieldValue ? moment(fieldValue) : '';
                    break;

                default:
                    initial[key] = fieldValue ?? '';
                    break;
            }
        });

        return initial;
    };


    const buildValidationSchema = (customFields) => {
        const schema = {
            name: yup
                .string()
                .required("Enter event subject")
                .min(2, "The name must be at least 2 characters.")
                .max(50, "The name must not be greater than 50 characters."),
            description: yup
                .string()
                .required("Enter event description")
                .min(2, "The description must be at least 2 characters.")
                .max(150, "The description must not be greater than 150 characters."),
            end_date: yup
                .string()
                .required("Enter Due date"),
        };

        customFields.forEach(field => {
            const key = field?.field_name;
            const label = field?.label || field?.field_name;
            const isRequired = field?.is_required === 'yes';

            switch (field?.display_type) {
                case 'text':
                case 'textarea':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'email':
                    schema[key] = yup.string().email('Enter a valid email');
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'phone':
                    schema[key] = yup
                        .string()
                        .matches(/^[0-9()+-\s]*$/, 'Enter a valid phone number');
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'number':
                    schema[key] = yup
                        .number()
                        .typeError(`${label} must be a number`);
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'select':
                    if (field?.is_multiple === 'yes') {
                        // MULTI-SELECT
                        schema[key] = yup.array().of(yup.string());
                        if (isRequired) {
                            schema[key] = schema[key].min(1, `Select at least one ${label}`);
                        }
                    } else {
                        // SINGLE SELECT
                        schema[key] = yup.string();
                        if (isRequired) {
                            schema[key] = schema[key].required(`${label} is required`);
                        }
                    }
                    break;
                case 'radio':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'checkbox':
                    schema[key] = yup.array();
                    if (isRequired) schema[key] = schema[key].min(1, `Select at least one ${label}`);
                    break;

                case 'date':
                case 'datetime':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'file':
                    schema[key] = yup.array().of(
                        yup
                            .mixed()
                            .test('fileSize', 'File too large', value => !value || value.size <= 4 * 1024 * 1024)
                    );
                    if (isRequired) {
                        schema[key] = schema[key].min(1, `Upload at least one ${label}`);
                    }
                    break;

                default:
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;
            }
        });

        return yup.object().shape(schema);
    };

    const staticFields = ['id', 'leadId', 'dealId', 'name', 'description', 'end_date', 'priority', 'activity_status'];

    const handleSubmit = async (values, onSubmitProps) => {
        const data = {
            id: taskList?.id,
            leadId: dataId,
            dealId: dataId,
            name: values?.name || '',
            description: values?.description || '',
            end_date: values.end_date
                ? moment.utc(values.end_date).tz(preTimeZoneKey).format('YYYY-MM-DD HH:mm:ss')
                : moment(new Date()).format("YYYY-MM-DD HH:mm:ss"),
            priority: values.priority,
            activity_status: values.activity_status,
        };

        const formData = new FormData();
        const customFieldsData = {};

        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value ?? '');
        });

        Object.entries(values).forEach(([key, value]) => {
            if (!staticFields.includes(key)) {
                const isFileArray = Array.isArray(value) && value.length && value[0] instanceof File;

                if (isFileArray) {
                    value.forEach((file, idx) => {
                        //formData.append(`${key}[${idx}]`, file);
                        formData.append(`${key}[]`, file);
                    });
                } else {
                    customFieldsData[key] = value;
                }
            }
        });

        // Append custom fields JSON string under one key
        formData.append('custom_fields', JSON.stringify(customFieldsData));


        await axiosInstance.post(`/api/${type}-task/${editId ? 'update' : 'store'}`, formData).then(response => {
            if (response.status === 200) {
                hideLoader()
                showSnackbar(response.data.message, 'success')
                onClose()
                handleReloadFunc()
            }
        }).then(json => {

        }).catch(error => {
            if (error.response) {
                hideLoader()
                if (error.response.status === 422 || error.response.data.validation_errors) {
                    const errors = error.response.data.validation_errors;
                    Object.keys(errors).forEach(key => {
                        onSubmitProps.setFieldError(key, errors[key][0]);
                    });

                } else {
                    showSnackbar(error.response.data.message, 'error')
                }
            }
        });
    }

    return (
        <>

            <Dialog onClose={onClose} open={open}>
                <div className="dialog-title">
                    <h2>{editId ? 'Edit' : 'Add'} Task</h2>
                    <Tooltip title="Close">
                        <button className="icon-button dialog-close" onClick={onClose}>
                            <Icon path={mdiClose} color={'currentColor'} size={1} />
                        </button>
                    </Tooltip>
                </div>
                <Formik
                    initialValues={buildInitialValues(customFields)}
                    validationSchema={buildValidationSchema(customFields)}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {props => {
                        const {
                            touched,
                            errors,
                            values,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue
                        } = props;
                        return (
                            <form onSubmit={handleSubmit}>
                                <TextField
                                    label="Subject"
                                    variant="filled"
                                    name="name"
                                    value={values?.name || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.name && touched.name}
                                    helperText={(errors.name && touched.name) && errors.name}
                                />

                                <TextField
                                    label="Description"
                                    variant="filled"
                                    name="description"
                                    value={values?.description || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    multiline
                                    rows={3}
                                    error={errors.description && touched.description}
                                    helperText={(errors.description && touched.description) && errors.description}
                                />


                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DateTimePicker
                                        label="Due Date"
                                        format={preferredDateFormat}
                                        views={["day", "hours", "minutes", "month", "year"]}
                                        name="end_date"
                                        slots={{
                                            popper: PickerWrapper
                                        }}
                                        slotProps={{
                                            textField: {
                                                variant: 'filled',
                                                name: "end_date",
                                                type: 'text',
                                                error: errors.end_date && touched.end_date,
                                                helperText: (errors.end_date && touched.end_date) && errors.end_date
                                            }
                                        }}
                                        value={values?.end_date ? moment.utc(values.end_date).tz(preTimeZoneKey) : moment()}
                                        onChange={(value) => setFieldValue("end_date", value, true)}
                                        onBlur={handleBlur}
                                    />

                                </LocalizationProvider>
                                <TextField
                                    select
                                    label="Priority"
                                    variant="filled"
                                    name="priority"
                                    value={values?.priority || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.priority && touched.priority}
                                    helperText={(errors.priority && touched.priority) && errors.priority}
                                >
                                    {
                                        Object.entries(priority).map(([key, value]) => (
                                            <MenuItem key={key} value={key}>{value}</MenuItem>
                                        ))
                                    }


                                </TextField>
                                <TextField
                                    select
                                    label="Status"
                                    variant="filled"
                                    name="activity_status"
                                    value={values?.activity_status || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.activity_status && touched.activity_status}
                                    helperText={(errors.activity_status && touched.activity_status) && errors.activity_status}
                                >
                                    {
                                        Object.entries(status).map(([key, value]) => (
                                            <MenuItem key={key} value={key}>{value}</MenuItem>
                                        ))
                                    }
                                </TextField>
                                {customFields?.length > 0 &&
                                    <Typography variant="title-medium-large-semibold" component={'h4'} className="mb-12">Custom Fields</Typography>
                                }
                                {customFields?.length > 0 && (
                                    customFields.map((field, idx) => (
                                        <DynamicFields
                                            key={idx}
                                            field={field}
                                            values={values}
                                            handleChange={handleChange}
                                            handleBlur={handleBlur}
                                            errors={errors}
                                            touched={touched}
                                            setFieldValue={setFieldValue}
                                            selectedFiles={editId ? taskList?.custom_field_values : []}
                                        />
                                    ))
                                )}
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Button className="btn-block" type="submit">{editId ? 'Edit' : 'Add'} Task</Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button color="secondary" className="btn-block" onClick={onClose}>Cancel</Button>
                                    </Grid>
                                </Grid>
                            </form>
                        );
                    }}
                </Formik>
            </Dialog>


        </>
    );
}

export default AddTaskDialog;